import { createPortal } from "react-dom";
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import {
  restrictToVerticalAxis,
  restrictToWindowEdges,
  snapCenterToCursor
} from "@dnd-kit/modifiers";
import { useState } from "react";

import { Flex, isNullish, usePortalContainer } from "@theorgchartdev/storybook";
import { useRefDict } from "@/hooks/useRefDict";
import { DragLayoutSegment } from "@/appComponents/Navigation/RightSidebar/BackgroundDisplay/DragLayoutSegment";
import { AreaAccordion } from "@/appComponents/Navigation/RightSidebar/BackgroundDisplay/AreaAccordion";
import { useTopic } from "@/hooks/useTopic";
import { enumHorzCentering, enumVertCentering } from "@coreapp/constants";

export const enumBGAreas = {
  topLeft: { horz: enumHorzCentering.pinLeft, vert: enumVertCentering.pinTop },
  topMiddle: { horz: enumHorzCentering.pinCenter, vert: enumVertCentering.pinTop },
  topRight: { horz: enumHorzCentering.pinRight, vert: enumVertCentering.pinTop },
  bottomLeft: { horz: enumHorzCentering.pinLeft, vert: enumVertCentering.pinBottom },
  bottomMiddle: { horz: enumHorzCentering.pinCenter, vert: enumVertCentering.pinBottom },
  bottomRight: { horz: enumHorzCentering.pinRight, vert: enumVertCentering.pinBottom },
  customPlacement: { horz: enumHorzCentering.userplacement, vert: enumVertCentering.userplacement }
};

export const BackgroundAreas = ({ backgroundName, backgroundArea, backgroundCtrl }) => {
  const accordionHandleCtrl = useRefDict();
  const portalContainer = usePortalContainer();
  const [activeDragData, setDragData] = useState(null);

  useTopic("expandBackgroundArea", area => {
    handleExpandArea(area);
  });

  const handleExpandArea = area => {
    const handleRef = accordionHandleCtrl.get(area);
    if (!handleRef?.isExpanded) handleRef?.expand();
  };

  const handleCollapseArea = area => {
    const handleRef = accordionHandleCtrl.get(area);
    if (handleRef?.isExpanded) handleRef?.collapse();
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    })
  );

  const handleDragOver = event => {
    const data = event.over?.data?.current;
    if (isNullish(data)) return;
    const { area } = data;
    handleExpandArea(area);
  };

  const handleDragStart = event => {
    const data = event.active?.data?.current;
    const { layout, area, layoutIndex } = data;
    setDragData({ layout, area, layoutIndex });
    backgroundCtrl.handleElementFocus(layout);
  };

  const handleDragEnd = event => {
    if (!activeDragData) {
      return;
    }
    const { layout } = activeDragData;
    const data = event.over?.data.current;

    const { area, layoutIndex } = data;
    let newIndex = 0;
    if (!isNullish(layoutIndex)) {
      newIndex = layoutIndex;
      if (activeDragData.area !== area) newIndex++;
    }

    var areaMap = enumBGAreas[area];

    if (!areaMap || (activeDragData.area !== "customPlacement" && area === "customPlacement"))
      return;

    let updatedLayout = layout.Clone({ includeId: true });

    updatedLayout.SetChartHorzCentering(areaMap.horz);
    updatedLayout.SetChartVertCentering(areaMap.vert);

    backgroundCtrl.handleInsertLayout(
      area,
      newIndex,
      updatedLayout,
      activeDragData.area,
      activeDragData.layoutIndex
    );

    handleExpandArea(area);
  };

  const areas = Object.keys(enumBGAreas);

  return (
    <DndContext
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      sensors={sensors}
    >
      <Flex direction={"column"} height={"100%"} overflow={"hidden"}>
        <Flex className={"ocn-list-scrollbar"} flex={"1"} direction={"column"} overflow={"auto"}>
          {areas.map((area, index) => (
            <AreaAccordion
              key={`${area}-${backgroundName}`}
              index={index}
              area={area}
              name={backgroundName}
              backgroundArea={backgroundArea}
              handleExpandArea={handleExpandArea}
              handleCollapseArea={handleCollapseArea}
              accordionHandle={accordionHandleCtrl}
              accordionHandleRef={accordionHandleCtrl.register(area)}
              handleLayoutChange={backgroundCtrl.handleLayoutChange(area)}
              handleLayoutAdd={backgroundCtrl.handleLayoutAdd}
              handleLayoutRemove={backgroundCtrl.handleLayoutRemove(area)}
            />
          ))}
        </Flex>
      </Flex>
      {activeDragData &&
        createPortal(
          <DragOverlay
            className={"oc-drag-overlay"}
            modifiers={[snapCenterToCursor, restrictToVerticalAxis, restrictToWindowEdges]}
            dropAnimation={null}
            style={{ height: "45px" }}
          >
            <DragLayoutSegment layout={activeDragData.layout} />
          </DragOverlay>,
          portalContainer ?? document.body
        )}
    </DndContext>
  );
};
